/**
 * Сервис для загрузки и кэширования каталогов Primar
 * 
 * Стратегия кэширования:
 * 1. LocalStorage (< 24 часов)
 * 2. HTTP ETag + If-None-Match (304 Not Modified)
 * 3. Browser Cache-Control (max-age=86400)
 */

import JSZip from 'jszip'
import { elBackAxios } from '@/services/myAxios'

const CATALOG_TYPES = ['countries', 'products', 'enc', 'encreplaces']
const CACHE_TTL = 86400000 // 24 часов в миллисекундах
const REQUEST_TIMEOUT = 60000 // 60 секунд
const MAX_RETRIES = 2

/**
 * Получить данные каталога с трёхуровневым кэшированием
 * 
 * @param {string} catalogType - Тип каталога: 'countries', 'products', 'enc', 'encreplaces'
 * @returns {Promise<object>} - Распарсенные JSON данные
 * @throws {Error} - Если загрузка или парсинг не удались
 */
export async function getCatalogData(catalogType) {
  if (!CATALOG_TYPES.includes(catalogType)) {
    throw new Error(`Invalid catalog type: ${catalogType}`)
  }

  const cacheKey = `catalog_${catalogType}`

  // === УРОВЕНЬ 1: Проверка LocalStorage (< 24 часов) ===
  const cached = localStorage.getItem(cacheKey)
  if (cached) {
    try {
      const cacheEntry = JSON.parse(cached)
      const age = Date.now() - cacheEntry.timestamp
      
      if (age < CACHE_TTL) {
        console.log(`📦 Using cached ${catalogType} (age: ${Math.round(age / 60000)} min)`)
        return cacheEntry.data
      }
    } catch (e) {
      console.warn(`Failed to parse cached ${catalogType}:`, e)
      localStorage.removeItem(cacheKey)
    }
  }

  // === УРОВЕНЬ 2: HTTP запрос с ETag + If-None-Match ===
  console.log(`📥 Downloading ${catalogType} from server...`)
  
  try {
    const data = await fetchCatalogWithRetry(catalogType, cached)
    return data
  } catch (error) {
    // === FALLBACK: Использовать старый кэш при ошибке сети ===
    if (cached && (error.name === 'NetworkError' || error.name === 'TimeoutError')) {
      try {
        const cacheEntry = JSON.parse(cached)
        const age = Date.now() - cacheEntry.timestamp
        const ageHours = Math.round(age / 3600000)
        
        console.warn(
          `⚠️ Network error, using stale cache for ${catalogType} (${ageHours} hours old):`,
          error.message
        )
        
        return cacheEntry.data
      } catch (e) {
        throw error
      }
    }
    
    throw error
  }
}

/**
 * Загрузить каталог с повторными попытками
 * @private
 */
async function fetchCatalogWithRetry(catalogType, cached, attempt = 0) {
  try {
    return await fetchCatalogFromServer(catalogType, cached)
  } catch (error) {
    if (attempt < MAX_RETRIES && (error.name === 'NetworkError' || error.name === 'TimeoutError')) {
      const delay = Math.pow(2, attempt) * 1000 // 1s, 2s, 4s
      console.warn(`Retry ${attempt + 1}/${MAX_RETRIES} in ${delay}ms...`)
      await new Promise(resolve => setTimeout(resolve, delay))
      return fetchCatalogWithRetry(catalogType, cached, attempt + 1)
    }
    throw error
  }
}

/**
 * Загрузить каталог с сервера
 * @private
 */
async function fetchCatalogFromServer(catalogType, cached) {
  const headers = {}
  
  // Если есть старые данные, отправить ETag для проверки обновления
  if (cached) {
    try {
      const cacheEntry = JSON.parse(cached)
      if (cacheEntry.etag) {
        headers['If-None-Match'] = cacheEntry.etag
      }
    } catch (e) {
      // ignore
    }
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    // Check if we are on the Chinese site (ecdis-link.net) to use mirror for catalog
    // For local testing on localhost, you can add ?use_mirror=true to the URL
    const isChinaSite = typeof window !== 'undefined' && (
      window.location.hostname.endsWith('ecdis-link.net') || 
      (process.env.NODE_ENV === 'development' && window.location.search.includes('use_mirror=true'))
    );
    
    // For China site, use static mirror for faster download
    const url = isChinaSite 
      ? `https://ecdis-link.net/catalog/${catalogType}.zip` 
      : `/api/catalog/${catalogType}`;

    let response;

    if (isChinaSite) {
      console.log(`🇨🇳 China site mirror: ${url}`);
      // Use vanilla fetch instead of elBackAxios to avoid Authorization header
      // This makes it a "simple request" and avoids CORS preflight (OPTIONS)
      const fetchResponse = await fetch(url, { 
        signal: controller.signal,
        method: 'GET',
        // No custom headers here to keep it a "simple request"
      });

      if (!fetchResponse.ok) {
        throw new Error(`Mirror fetch failed: ${fetchResponse.status} ${fetchResponse.statusText}`);
      }

      const blob = await fetchResponse.blob();
      
      // Map fetch response to look like axios response for the rest of the logic
      response = {
        data: blob,
        headers: {
          'etag': fetchResponse.headers.get('etag'),
          'x-catalog-version': fetchResponse.headers.get('x-catalog-version'),
          'x-catalog-date': fetchResponse.headers.get('x-catalog-date')
        },
        status: fetchResponse.status
      };
    } else {
      // Use elBackAxios for standard API requests
      response = await elBackAxios.get(url, {
        headers,
        signal: controller.signal,
        responseType: 'blob'
      })
    }

    clearTimeout(timeoutId)

    // === 304 Not Modified — данные не изменились ===
    // Note: axios doesn't return 304 responses as successful, so this branch may not be reached
    // but keeping the logic for reference
    if (response.status === 304) {
      console.log(`✅ Server returned 304 Not Modified for ${catalogType}`)
      
      if (cached) {
        const cacheEntry = JSON.parse(cached)
        // Обновить timestamp для сброса счётчика 24 часов
        cacheEntry.timestamp = Date.now()
        localStorage.setItem(`catalog_${catalogType}`, JSON.stringify(cacheEntry))
        return cacheEntry.data
      }
    }

    // === 200 OK — новые данные ===
    const blob = response.data

    // === Распаковка ZIP и парсинг JSON ===
    const zip = await JSZip.loadAsync(blob)

    const jsonFileName = `${catalogType}.json`
    const jsonFile = zip.file(jsonFileName)

    if (!jsonFile) {
      throw new Error(`ZIP does not contain ${jsonFileName}`)
    }

    const jsonText = await jsonFile.async('string')
    let data

    try {
      data = JSON.parse(jsonText)
    } catch (parseError) {
      throw new Error(`Failed to parse ${jsonFileName}: ${parseError.message}`)
    }

    // === Сохранение в LocalStorage ===
    const etag = response.headers['etag']
    const cacheEntry = {
      data,
      etag,
      timestamp: Date.now(),
      version: response.headers['x-catalog-version'],
      catalogDate: response.headers['x-catalog-date']
    }

    try {
      localStorage.setItem(`catalog_${catalogType}`, JSON.stringify(cacheEntry))
      console.log(`✅ Cached ${catalogType}`)
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn(`⚠️ LocalStorage quota exceeded for ${catalogType}, continuing without cache`)
      } else {
        throw e
      }
    }

    return data
  } catch (error) {
    clearTimeout(timeoutId)

    // Преобразовать AbortError в TimeoutError
    if (error.name === 'AbortError') {
      const timeoutError = new Error(`Request timeout for ${catalogType}`)
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }

    throw error
  }
}

/**
 * Очистить кэш каталога
 * @param {string|null} catalogType - Если null, очистить все
 */
export function clearCatalogCache(catalogType = null) {
  if (catalogType) {
    localStorage.removeItem(`catalog_${catalogType}`)
    console.log(`🗑️ Cleared cache for ${catalogType}`)
  } else {
    CATALOG_TYPES.forEach(type => {
      localStorage.removeItem(`catalog_${type}`)
    })
    console.log('🗑️ Cleared all catalog caches')
  }
}

/**
 * Получить информацию о статусе кэша
 * @returns {object} - Статус кэша для всех типов
 */
export function getCacheStatus() {
  const status = {}

  CATALOG_TYPES.forEach(type => {
    const cacheKey = `catalog_${type}`
    const cached = localStorage.getItem(cacheKey)

    if (cached) {
      try {
        const cacheEntry = JSON.parse(cached)
        const age = Date.now() - cacheEntry.timestamp
        const isFresh = age < CACHE_TTL
        const itemCount = Array.isArray(cacheEntry.data) ? cacheEntry.data.length : 'N/A'

        status[type] = {
          cached: true,
          age,
          ageMins: Math.round(age / 60000),
          isFresh,
          version: cacheEntry.version || 'N/A',
          itemCount,
          etag: cacheEntry.etag ? '✓' : '✗'
        }
      } catch (e) {
        status[type] = { cached: false, error: e.message }
      }
    } else {
      status[type] = { cached: false }
    }
  })

  return status
}
